import usePopularCategories from "../../hooks/usePopularCategories";
import { useState } from "react";
import useNavigationMemory from "../../hooks/useNavigationMemory";
import { useNavigate } from "react-router-dom";
import imagePlaceholder from "../../assets/image-placeholder.webp";
import { ArrowForward, CalendarToday, Inventory } from "@mui/icons-material";
import {
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
} from "@mui/material";

export const PopularCategories = ({ mode = "normal" }) => {
  const {
    popularCategories,
    loading: loadingCategories,
    error: errorCategories,
  } = usePopularCategories();
  const navigate = useNavigate();
  const { markNavigationFromHome } = useNavigationMemory();

  // Manejar navegación a categoría con tracking
  const handleCategoryNavigation = (categoryName) => {
    const filters = { category: categoryName };

    // Marcar que la navegación viene desde Home con filtros
    markNavigationFromHome(filters);

    // Navegar al catálogo con la categoría
    navigate(`/catalogo?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <>
      {loadingCategories ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={60} />
        </Box>
      ) : errorCategories ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error">
            Error cargando categorías populares
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {popularCategories.map((category, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={category.id}>
              <Card
                key={{ index }}
                sx={{
                  cursor: "pointer",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 3,
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  border: "1px solid rgba(25, 118, 210, 0.1)",
                  "&:hover": {
                    transform: "translateY(-3px) scale(1.01)",
                    boxShadow: "0 15px 20px rgba(25, 118, 210, 0.15)",
                    borderColor: "primary.main",
                    "& .category-image": {
                      transform: "scale(1.1)",
                    },
                    "& .category-overlay": {
                      opacity: 0.05,
                    },
                  },
                }}
                onClick={() => handleCategoryNavigation(category.name)}
              >
                {/* Imagen de la categoría */}
                <Box
                  sx={{
                    height: mode === "normal" ? 180 : 100,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <CardMedia
                    component="img"
                    height={mode === "normal" ? 180 : 100}
                    width={mode === "normal" ? 180 : 100}
                    image={category.image || imagePlaceholder}
                    alt={category.name}
                    title={category.name}
                    className="category-image"
                    sx={{
                      objectFit: "cover",
                      transition: "transform 0.4s ease",
                      filter: "brightness(0.9)",
                    }}
                    onError={(e) => {
                      //si la imagen no carga, poner placeholder
                      e.target.onerror = null;
                      e.target.src = imagePlaceholder;
                    }}
                  />

                  {/* Overlay con gradiente */}
                  <Box
                    className="category-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    }}
                  />

                  {/* Badge de popularidad */}
                  {category.featured && (
                    <Chip
                      label={mode !== "minimal" ? "⭐ Destacada" : "⭐"}
                      size="small"
                      sx={{
                        position: "absolute",
                        opacity: 1,
                        top: mode === "normal" ? 12 : 3,
                        right: mode === "normal" ? 12 : 3,
                        backgroundColor:
                          mode === "normal" ? "#d32f2f" : "#d32f2f50",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        zIndex: 2,
                      }}
                    />
                  )}

                  {/* Estadísticas sobre la imagen */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: mode === "normal" ? 12 : 3,
                      left: mode === "normal" ? 12 : 3,

                      display: "flex",
                      gap: 1,
                      zIndex: 2,
                    }}
                  >
                    <Chip
                      icon={
                        <Typography>
                          <Inventory sx={{ fontSize: "12px" }} />
                        </Typography>
                      }
                      label={
                        mode !== "minimal" ? (
                          <Typography fontWeight={"bold"} fontSize={"0.8rem"}>
                            {category.productCount > 99
                              ? "99+"
                              : category.productCount}{" "}
                            {category.name}
                          </Typography>
                        ) : (
                          <Typography fontWeight={"bold"} fontSize={"0.8rem"}>
                            {category.productCount > 99
                              ? "99+"
                              : category.productCount}
                          </Typography>
                        )
                      }
                      size="small"
                      sx={{
                        backgroundColor: "#004b93",
                        color: "white",
                        fontWeight: "medium",
                        backdropFilter: "blur(4px)",
                        "& .MuiChip-icon": {
                          color: "white",
                        },
                      }}
                    />

                    {/* <Chip
                          label={`${Math.round(category.percentage)}%`}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255, 0, 51, 0.9)",
                            color: "white",
                            fontWeight: "bold",
                            backdropFilter: "blur(4px)",
                          }}
                        /> */}
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 2,
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      gap: 1,
                      zIndex: 2,
                    }}
                  >
                    {mode === "minimal" && (
                      <Chip
                        label={
                          <Typography
                            variant="span"
                            component={"span"}
                            fontWeight="bold"
                            gutterBottom
                            sx={{
                              color: "white",
                              fontSize: "0.8rem",
                            }}
                          >
                            {category.name}
                          </Typography>
                        }
                        size="small"
                        sx={{
                          backgroundColor: "#004b9300",
                          color: "white",
                          backdropFilter: "blur(20px)",
                          "& .MuiChip-icon": {
                            color: "white",
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {mode === "normal" && (
                  <CardContent sx={{ p: 3 }}>
                    {/* Nombre de la categoría */}
                    <Typography
                      variant="h6"
                      component={"h5"}
                      fontWeight="bold"
                      gutterBottom
                      sx={{
                        color: "text.primary",
                        fontSize: "1.25rem",
                        lineHeight: 1.3,
                        mb: 2,
                      }}
                    >
                      {category.name}
                    </Typography>

                    {/* Descripción */}
                    {/* <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 3,
                        fontSize: "0.9rem",
                        lineHeight: 1.5,
                        minHeight: 40,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        opacity: category.description ? 1 : 0,
                      }}
                    >
                      {category.description || "Descripción no disponible"}
                    </Typography> */}

                    {/* Información adicional */}
                    <Box sx={{ mb: 3 }}>
                      {/* Precio promedio */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1.5,
                          opacity:
                            category.averagePrice &&
                            parseInt(category.averagePrice) > 0
                              ? 1
                              : 0,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Precio promedio:
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          $
                          {category.averagePrice
                            ? (
                                parseInt(category.averagePrice) / 1000000
                              ).toFixed(1)
                            : "0.0"}
                          M
                        </Typography>
                      </Box>

                      {/* Marcas populares */}
                      <Box
                        sx={{
                          mb: 1.5,
                          minHeight: 40,
                          opacity:
                            category.popularBrands &&
                            category.popularBrands.length > 0
                              ? 1
                              : 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 0.5 }}
                        >
                          Marcas populares:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {category.popularBrands &&
                          category.popularBrands.length > 0 ? (
                            <>
                              {category.popularBrands
                                .slice(0, 3)
                                .map((brand, idx) => (
                                  <Chip
                                    key={idx}
                                    label={brand}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: "0.7rem",
                                      height: 20,
                                      "& .MuiChip-label": {
                                        px: 1,
                                      },
                                    }}
                                  />
                                ))}
                              {category.popularBrands.length > 3 && (
                                <Chip
                                  label={`+${
                                    category.popularBrands.length - 3
                                  }`}
                                  size="small"
                                  variant="filled"
                                  color="primary"
                                  sx={{
                                    fontSize: "0.7rem",
                                    height: 20,
                                    "& .MuiChip-label": {
                                      px: 1,
                                    },
                                  }}
                                />
                              )}
                            </>
                          ) : (
                            <Chip
                              label="No disponible"
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                "& .MuiChip-label": {
                                  px: 1,
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* Años recientes */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1.5,
                          opacity:
                            category.yearRange &&
                            category.yearRange.min &&
                            category.yearRange.max
                              ? 1
                              : 0,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          <CalendarToday sx={{ fontSize: 12, mr: 0.5 }} />
                          Años disponibles:
                        </Typography>
                        <Typography variant="caption" fontWeight="medium">
                          {category.yearRange &&
                          category.yearRange.min &&
                          category.yearRange.max
                            ? `Desde ${category.yearRange.min} hasta ${category.yearRange.max}`
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Rango de precios mejorado */}
                    <Box
                      sx={{
                        backgroundColor: "rgba(25, 118, 210, 0.05)",
                        borderRadius: 2,
                        p: 2,
                        border: "1px solid rgba(25, 118, 210, 0.1)",
                        mb: 3,
                        minHeight: 80,
                        opacity:
                          category.priceRange &&
                          parseInt(category.priceRange.min) > 0
                            ? 1
                            : 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 1 }}
                      >
                        Rango de Precios
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ textAlign: "left" }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="success.main"
                          >
                            $
                            {category.priceRange
                              ? (
                                  parseInt(category.priceRange.min) / 1000000
                                ).toFixed(1)
                              : "0.0"}
                            M
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Desde
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            mx: 2,
                            height: 2,
                            backgroundColor: "primary.main",
                            borderRadius: 1,
                            opacity: 0.3,
                          }}
                        />
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="error.main"
                          >
                            $
                            {category.priceRange
                              ? (
                                  parseInt(category.priceRange.max) / 1000000
                                ).toFixed(1)
                              : "0.0"}
                            M
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Hasta
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Botón de acción */}
                    <Button
                      variant="outlined"
                      fullWidth
                      endIcon={<ArrowForward />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: "bold",
                        borderColor: "primary.main",
                        color: "primary.main",
                        "&:hover": {
                          backgroundColor: "primary.main",
                          color: "white",
                          transform: "translateY(-0.5px)",
                          boxShadow: "0 6px 16px rgba(25, 118, 210, 0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Explorar {category.name}
                    </Button>
                  </CardContent>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};
